@if (displayState === 'EDITOR' && editor !== null) {
  @let isMaximized = (isEditorMaximized$ | async) ?? false;
  <div class="editor-container" [class.maximized]="isMaximized" [style.--editor-zoom]="isMaximized ? ((zoomLevel$ | async) ?? 1) : 1">
    <div class="editor-main">
      <app-agenda-missing-warning [call]="call" (openAgendaUpload)="openAgendaUpload.emit()"></app-agenda-missing-warning>
      @if (showTrackChangesToolbar$ | async) {
        @let navCount = (navigationTargetCount$ | async) ?? 0;
        <div class="track-changes-toolbar">
          <button mat-stroked-button type="button" [disabled]="navCount === 0" (click)="gotoPreviousChange()">
            <svg
              class="track-changes-toolbar-icon-leading"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M13 3v4a1 1 0 0 0 1 1h4" />
              <g class="track-changes-icon-accent">
                <circle cx="7.5" cy="11" r="0.6" />
                <line x1="9.25" y1="11" x2="13" y2="11" />
                <circle cx="7.5" cy="14" r="0.6" />
                <line x1="9.25" y1="14" x2="12" y2="14" />
                <circle cx="7.5" cy="17" r="0.6" />
                <line x1="9.25" y1="17" x2="11" y2="17" />
              </g>
              <path class="track-changes-icon-accent" d="M21 18.5 H14 M16.5 16 L14 18.5 L16.5 21" />
            </svg>
            {{ 'EDITOR.TRACK_CHANGES.PREVIOUS' | translate }}
          </button>
          <button mat-stroked-button type="button" [disabled]="navCount === 0" (click)="gotoNextChange()">
            <svg
              class="track-changes-toolbar-icon-leading"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M13 3v4a1 1 0 0 0 1 1h4" />
              <g class="track-changes-icon-accent">
                <circle cx="7.5" cy="11" r="0.6" />
                <line x1="9.25" y1="11" x2="13" y2="11" />
                <circle cx="7.5" cy="14" r="0.6" />
                <line x1="9.25" y1="14" x2="12" y2="14" />
                <circle cx="7.5" cy="17" r="0.6" />
                <line x1="9.25" y1="17" x2="11" y2="17" />
              </g>
              <path class="track-changes-icon-accent" d="M14 18.5 H21 M18.5 16 L21 18.5 L18.5 21" />
            </svg>
            {{ 'EDITOR.TRACK_CHANGES.NEXT' | translate }}
          </button>
          <span class="track-changes-toolbar-count">
            @if (navCount === 0) {
              {{ 'EDITOR.TRACK_CHANGES.NAV_NONE' | translate }}
            } @else if (navCount === 1) {
              {{ 'EDITOR.TRACK_CHANGES.NAV_COUNT' | translate: { count: navCount } }}
            } @else {
              {{ 'EDITOR.TRACK_CHANGES.NAV_COUNT_PLURAL' | translate: { count: navCount } }}
            }
          </span>
        </div>
      }
      @if ((findReplaceOpen$ | async) && findReplaceTarget !== null) {
        <app-find-replace-panel
          class="find-replace-panel-host"
          [target]="findReplaceTarget"
          [canUserEdit]="canUserEdit"
          (closed)="closeFindReplace()"
          (replaceRequested)="replaceCurrentMatch()"
          (replaceAllRequested)="replaceAllMatches()"
        ></app-find-replace-panel>
      }
      @let viewedRevision = viewedRevision$ | async;
      @if (viewedRevision) {
        <div class="revision-banner">
          <mat-icon class="revision-banner-icon">history</mat-icon>
          <span class="revision-banner-text">{{
            'EDITOR.REVISION_HISTORY.VIEWING_BANNER'
              | translate: { date: (viewedRevision.createdAt | date: 'd MMM y, HH:mm'), author: viewedRevision.authorName }
          }}</span>
          @if (canUserEdit) {
            <button mat-flat-button type="button" class="revision-banner-restore" (click)="restoreSelectedRevision()">
              {{ 'EDITOR.REVISION_HISTORY.RESTORE' | translate }}
            </button>
          }
          <button mat-button type="button" (click)="backToCurrent()">{{ 'EDITOR.REVISION_HISTORY.BACK_TO_CURRENT' | translate }}</button>
        </div>
      }
      <tiptap-editor class="editor" [style.display]="viewedRevision ? 'none' : null" [editor]="editor"></tiptap-editor>
      @if (viewedRevision && previewEditor !== null) {
        <tiptap-editor class="editor" [editor]="previewEditor"></tiptap-editor>
      }
      <tiptap-bubble-menu [editor]="editor" [options]="bubbleMenuOptions" [shouldShow]="shouldShowFormattingMenu" [updateDelay]="100">
        <div class="editor-bubble-menu">
          @if (canModifyMinutes) {
            <button mat-icon-button type="button" (click)="toggleBoldOnSelection()">
              <mat-icon>format_bold</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="toggleItalicOnSelection()">
              <mat-icon>format_italic</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="toggleHighlightOnSelection()">
              <mat-icon fontSet="material-symbols-outlined">ink_highlighter</mat-icon>
            </button>
            <div class="text-color-split-button">
              <button
                mat-icon-button
                type="button"
                class="text-color-apply-button"
                [attr.aria-label]="'EDITOR.TEXT_COLOR.APPLY_TOOLTIP' | translate"
                [matTooltip]="'EDITOR.TEXT_COLOR.APPLY_TOOLTIP' | translate"
                (click)="applyCurrentTextColor()"
              >
                <mat-icon [style.color]="currentTextColor">format_color_text</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                class="text-color-dropdown-toggle"
                [attr.aria-label]="'EDITOR.TEXT_COLOR.PALETTE_TOOLTIP' | translate"
                [matTooltip]="'EDITOR.TEXT_COLOR.PALETTE_TOOLTIP' | translate"
                [matMenuTriggerFor]="textColorMenu"
              >
                <mat-icon>arrow_drop_down</mat-icon>
              </button>
            </div>
            <mat-menu #textColorMenu="matMenu" class="text-color-menu">
              @for (color of textColorOptions; track color.value) {
                <button mat-menu-item type="button" (click)="selectTextColor(color.value)">
                  <span class="text-color-swatch" [style.background-color]="color.value"></span>
                  {{ color.labelKey | translate }}
                </button>
              }
            </mat-menu>
            <button mat-icon-button type="button" [disabled]="bulletListDisabled" (click)="toggleBulletList()">
              <mat-icon>format_list_bulleted</mat-icon>
            </button>
            <button mat-icon-button type="button" [disabled]="orderedListDisabled" (click)="toggleOrderedList()">
              <mat-icon>format_list_numbered</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="decreaseIndent()">
              <mat-icon>format_indent_decrease</mat-icon>
            </button>
            <button mat-icon-button type="button" [disabled]="increaseIndentDisabled" (click)="increaseIndent()">
              <mat-icon>format_indent_increase</mat-icon>
            </button>
          }
          @if (canUserComment) {
            @if (canModifyMinutes) {
              <div class="bubble-menu-divider"></div>
            }
            <button
              mat-icon-button
              type="button"
              [attr.aria-label]="'EDITOR.COMMENTS.ADD' | translate"
              [matTooltip]="'EDITOR.COMMENTS.ADD' | translate"
              (click)="addComment()"
            >
              <mat-icon fontSet="material-symbols-outlined">add_comment</mat-icon>
            </button>
          }
          @if (canUserEdit) {
            <div class="bubble-menu-divider"></div>
            <button mat-button type="button" [matMenuTriggerFor]="aiMenu">
              <mat-icon class="ai-actions-icon">auto_awesome</mat-icon>
              {{ 'EDITOR.AI_ACTIONS' | translate }}
            </button>
            <mat-menu #aiMenu="matMenu" class="ai-actions-menu">
              @for (actionConfig of aiActionsConfig; track actionConfig.action) {
                <button mat-menu-item (click)="performAiAction(actionConfig.action)">
                  <mat-icon>{{ actionConfig.icon }}</mat-icon>
                  {{ actionConfig.i18nKey | translate }}
                </button>
              }
            </mat-menu>
          }
        </div>
      </tiptap-bubble-menu>
      <tiptap-bubble-menu
        [editor]="editor"
        [pluginKey]="suggestionPopupPluginKey"
        [options]="suggestionPopupOptions"
        [shouldShow]="shouldShowSuggestionPopup"
        [updateDelay]="50"
      >
        @if (activeSuggestion$ | async; as suggestion) {
          <app-suggestion-popup
            [suggestionId]="suggestion.suggestionId"
            [authorName]="suggestion.authorName"
            [createdAt]="suggestion.createdAt"
            [insertionRuns]="suggestion.insertionRuns"
            [deletionRuns]="suggestion.deletionRuns"
            [formattingAdded]="suggestion.formattingAdded"
            [formattingRemoved]="suggestion.formattingRemoved"
            [listChanges]="suggestion.listChanges"
            [colorChange]="suggestion.colorChange"
            [canResolve]="canUserEdit"
            (accept)="acceptSuggestion($event)"
            (reject)="rejectSuggestion($event)"
            (comment)="openSuggestionComments($event)"
          ></app-suggestion-popup>
        }
      </tiptap-bubble-menu>
      <tiptap-bubble-menu
        [editor]="editor"
        [pluginKey]="commentPopupPluginKey"
        [options]="suggestionPopupOptions"
        [shouldShow]="shouldShowCommentPopup"
        [updateDelay]="50"
      >
        @if (activeCommentPopup$ | async; as commentPopupAnchorId) {
          <app-comment-popup
            [anchorId]="commentPopupAnchorId"
            [thread]="(activeCommentPopupThread$ | async) ?? null"
            (viewThread)="onViewThread($event)"
          ></app-comment-popup>
        }
      </tiptap-bubble-menu>
      @if (showWordCount$ | async) {
        <mat-chip class="word-count-badge">
          <span class="word-count">{{ editor.storage.characterCount.words() }}</span> {{ 'EDITOR.WORD_COUNT.LABEL' | translate }}
        </mat-chip>
      }
    </div>
    @if (revisionHistoryOpen$ | async) {
      <app-tiptap-revision-history
        class="editor-sidebar"
        [revisions]="(revisions$ | async) ?? []"
        [selectedId]="(selectedRevisionId$ | async) ?? null"
        [filterSystemOnly]="(filterSystemOnly$ | async) ?? false"
        [loading]="(revisionsLoading$ | async) ?? false"
        (select)="selectRevision($event)"
        (filterChange)="setFilterSystemOnly($event)"
        (close)="toggleRevisionHistory()"
      ></app-tiptap-revision-history>
    } @else if (isMaximized && (hasSidebarContent$ | async)) {
      <app-tiptap-editor-sidebar
        class="editor-sidebar"
        [suggestions$]="suggestions$"
        [activeSuggestionId$]="activeSuggestionId$"
        [threads$]="threads$"
        [commentAnchors$]="commentAnchors$"
        [activeCommentAnchorId$]="activeCommentAnchorId$"
        [activeCommentDraftId$]="activeCommentDraftId$"
        [canResolve]="canUserEdit"
        [canResolveComment]="canUserComment"
        [currentUserId]="currentUserId"
        [canSuggest]="canUserSuggest"
        [canResolveAnyThread]="canResolveAnyThread"
        [currentUser]="threadCurrentUser"
        [canPostComment]="canPostComment"
        [filter]="(sidebarFilter$ | async) ?? 'all'"
        (filterChange)="setSidebarFilter($event)"
        (accept)="acceptSuggestion($event)"
        (reject)="rejectSuggestion($event)"
        (discard)="discardSuggestion($event)"
        (view)="viewSuggestion($event)"
        (submitComment)="onCardSubmitComment($event.suggestionId, $event.body)"
        (viewComment)="viewCommentAnchor($event)"
        (resolveComment)="resolveCommentThread($event)"
        (discardComment)="discardCommentDraft($event)"
        (submitCommentText)="onCommentSubmit($event.anchorId, $event.body)"
      ></app-tiptap-editor-sidebar>
    }
  </div>
} @else if (displayState === 'SYNCING') {
  <div class="centered-content">
    <app-progress-loader class="minutes-progress" indeterminate></app-progress-loader>
    <span class="loading-text">{{ 'CALLS.MINUTES_LOADING_MESSAGE' | translate }}</span>
  </div>
} @else if (displayState === 'PROCESSING_WITH_PROGRESS' || displayState === 'PROCESSING') {
  <app-minutes-generation-progress [call]="call"></app-minutes-generation-progress>
} @else if (displayState === 'NO_SPEECH') {
  <div class="centered-content">
    <h1>{{ 'CALLS.NO_SPEECH_MESSAGE' | translate }}</h1>
  </div>
} @else {
  <div class="centered-content">
    <h1>{{ 'CALLS.MINUTES_WILL_BE_SHOWN_HERE' | translate }}</h1>
  </div>
}
